'use client'

import { Search } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import {
  Checkbox,
  FILTER_CHECKBOX_CLASS,
  FILTER_CHECKBOX_ICON_CLASS,
} from '@/components/ui/checkbox'
import { Select, SelectContent, SelectTrigger } from '@/components/ui/select'
import { ORDER_FORM_FIELD_CLASS } from '@/features/order/order-form/components/widget/order-form-fields'
import { useCanAutoFocusSearch } from '@/hooks/use-screen'
import { cn } from '@/lib/utils'
import { useGetRfqMakers } from '@/service/rfq.hook'

interface RfqMakerPickerProps {
  accountId: string
  selected: string[]
  onChange: (makerIds: string[]) => void
  /** True when the ticket's legs resolve to more than one account — there is no
   *  single account to query makers for, so the picker stays closed/read-only
   *  until the split resolves (see RfqParams' hasMixedAccounts). */
  disabled?: boolean
}

/**
 * Targeted-maker picker — a searchable, scrollable, multi-select dropdown. The maker
 * directory is fetched lazily once the selector opens and cached for the session.
 * Empty selection = all makers.
 */
export function RfqMakerPicker({ accountId, selected, onChange, disabled }: RfqMakerPickerProps) {
  const canAutoFocusSearch = useCanAutoFocusSearch()
  const t = useTranslations('orderForm')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const {
    data: makers = [],
    isLoading,
    isError,
    refetch,
  } = useGetRfqMakers(accountId, { enabled: open && !disabled })

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? makers.filter((m) => m.toLowerCase().includes(q)) : makers
  }, [makers, query])

  const toggle = (maker: string) =>
    onChange(selected.includes(maker) ? selected.filter((m) => m !== maker) : [...selected, maker])

  const label = selected.length === 0 ? t('allMakers') : t('makerCount', { count: selected.length })

  return (
    <Select
      open={open && !disabled}
      onOpenChange={(next) => {
        if (disabled) return
        if (!next) setQuery('')
        setOpen(next)
      }}
    >
      <SelectTrigger
        readonly={disabled}
        className={cn(
          ORDER_FORM_FIELD_CLASS,
          'w-full disabled:cursor-not-allowed disabled:opacity-50'
        )}
      >
        <span className="truncate">{disabled ? t('rfqMixedAccountsLabel') : label}</span>
      </SelectTrigger>
      <SelectContent
        align="start"
        className="z-[60]"
        viewportClassName="h-auto! max-h-72 gap-0! p-0"
      >
        <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
          <Search className="text-foreground/50 size-3.5 shrink-0" />
          <input
            autoFocus={canAutoFocusSearch}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(event) => {
              event.stopPropagation()
              if (!event.nativeEvent.isComposing && event.key === 'Enter') event.preventDefault()
            }}
            placeholder={t('searchMakers')}
            className="placeholder:text-foreground/40 w-full bg-transparent text-sm outline-none"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {isLoading ? (
            <p className="text-muted-foreground px-2 py-4 text-center text-xs">
              {t('loadingMakers')}
            </p>
          ) : isError ? (
            <div className="flex flex-col items-center gap-2 px-2 py-4">
              <p className="text-muted-foreground text-center text-xs">{t('makersUnavailable')}</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="text-chart-mint text-xs hover:underline"
              >
                {t('retry')}
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground px-2 py-4 text-center text-xs">
              {makers.length === 0 ? t('noMakersListed') : t('noMatches')}
            </p>
          ) : (
            filtered.map((maker) => {
              const isSelected = selected.includes(maker)
              return (
                <label
                  key={maker}
                  className={cn(
                    'focus-within:bg-foreground/10 hover:bg-foreground/10 flex w-full cursor-pointer items-center gap-3 px-2.5 py-1 text-left text-sm outline-none',
                    isSelected && 'bg-foreground/10'
                  )}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggle(maker)}
                    className={FILTER_CHECKBOX_CLASS}
                    iconClassName={FILTER_CHECKBOX_ICON_CLASS}
                  />
                  <span className="truncate">{maker}</span>
                </label>
              )
            })
          )}
        </div>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-muted-foreground hover:text-foreground border-foreground/10 w-full border-t px-3 py-2 text-left text-xs"
          >
            {t('clearUseAllMakers')}
          </button>
        )}
      </SelectContent>
    </Select>
  )
}
